import { offlineDb, type MutationQueueItem } from './db'
import { v4 as uuidv4 } from 'uuid'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'

export type EnqueueItem = Omit<MutationQueueItem, 'id' | 'clientId' | 'createdAt' | 'retryCount'>

async function addToQueue(item: EnqueueItem): Promise<void> {
  await offlineDb.mutationQueue.add({
    ...item,
    clientId: uuidv4(),
    createdAt: Date.now(),
    retryCount: 0,
  })
}

export async function hasQueuedCompletion(shiftId: number): Promise<boolean> {
  const queued = await offlineDb.mutationQueue.where('entity').equals('completeShift').toArray()
  return queued.some(item => item.shiftId === shiftId)
}

export async function queuedItemFor(
  entity: MutationQueueItem['entity'],
  entityId: number | undefined,
): Promise<MutationQueueItem | undefined> {
  if (entityId === undefined) return undefined

  const matches = await offlineDb.mutationQueue
    .where('[entity+entityId]')
    .equals([entity, entityId])
    .toArray()

  return matches[0]
}

export async function enqueue(incoming: EnqueueItem): Promise<void> {
  if (incoming.data !== undefined) {
    incoming = { ...incoming, data: convertDatesToUTC(incoming.data) }
  }

  await offlineDb.transaction('rw', offlineDb.mutationQueue, async () => {
    let existing: MutationQueueItem[] = []

    if (incoming.entityId !== undefined) {
      existing = await offlineDb.mutationQueue
        .where('[entity+entityId]')
        .equals([incoming.entity, incoming.entityId])
        .toArray()
    } else if (incoming.tempId) {
      existing = await offlineDb.mutationQueue
        .where('[entity+tempId]')
        .equals([incoming.entity, incoming.tempId])
        .toArray()
    }

    if (existing.length === 0) {
      await addToQueue(incoming)
      return
    }

    const prev = existing[0]

    if (incoming.operation === 'delete' && prev.operation === 'create') {
      await offlineDb.mutationQueue.delete(prev.id!)
      return
    }

    if (incoming.operation === 'update' && prev.operation === 'create') {
      await offlineDb.mutationQueue.update(prev.id!, { data: incoming.data })
      return
    }

    if (incoming.operation === 'update' && prev.operation === 'update') {
      await offlineDb.mutationQueue.update(prev.id!, {
        clientId: uuidv4(),
        data: incoming.data,
        rowVersion: incoming.rowVersion,
      })
      return
    }

    if (incoming.operation === 'delete' && prev.operation === 'update') {
      await offlineDb.mutationQueue.delete(prev.id!)
      await addToQueue(incoming)
      return
    }

    await addToQueue(incoming)
  })
}
